import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HelpCircle, Loader2, Play } from 'lucide-react';
import { TaxonomyTreeTab } from './TaxonomyTreeTab';
import { MappingsTab } from './MappingsTab';
import { ReviewQueueTab } from './ReviewQueueTab';
import { BalanceChartsTab } from './BalanceChartsTab';
import { AuditLogTab } from './AuditLogTab';
import { FeedbackTab } from './FeedbackTab';
import { SimilarityHeatmapTab } from './SimilarityHeatmapTab';
import { RebalanceTriggerModal } from './RebalanceTriggerModal';
import { TaxonomyHelpPanel } from './TaxonomyHelpPanel';
import {
  useTaxonomyList,
  useMappingsList,
  useBalanceStats,
} from './useSectorTaxonomy';
import {
  TAXONOMY_TABS,
  shiftIsoDate,
  todayIsoDate,
  type TaxonomyTabId,
} from './sectorTaxonomy.types';
import { subscribe } from '../../hooks/useWebSocket';
import { useRealtimeJobs } from '../../hooks/useRealtimeJobs';
import '../PendingCAEvents/pendingCa.css';
import './sectorTaxonomy.css';

// 권위: ARCH_DASHBOARD.md §4.11
//
// 레이아웃:
//   상단 — 요약 배너 (정규 섹터 N / 원천 섹터 M / 통합도 K / pending P / 신규 Q) + Type-A 실행 버튼
//   하단 — 5탭: 정규 섹터 트리 / 매핑 테이블 / 리뷰 큐 / 밸런싱 지표 / 감사 이력
//
// WebSocket: /topic/sector-taxonomy 구독 (신규 Type-C pending / 부트스트랩 완료)

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

interface TaxonomyEventPayload {
  event_code?: string;
  mapping_id?: number;
  source_sector_name?: string;
  decision?: string;
  confidence?: number;
}

export default function SectorTaxonomyPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL 쿼리(`tab=...`) 가 탭 상태의 단일 진실원. useState 로 복제하면
  // 외부 링크(FeedbackTab targetRef 딥링크 등) 가 URL 만 바꿀 때 탭 전환이
  // 안 되는 문제가 발생 → 매 렌더에서 파생값으로 계산.
  const tabFromUrl = searchParams.get('tab') as TaxonomyTabId | null;
  const activeTab: TaxonomyTabId =
    tabFromUrl && TAXONOMY_TABS.some((t) => t.id === tabFromUrl)
      ? tabFromUrl
      : 'tree';
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [rebalanceOpen, setRebalanceOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // 실행 중 bootstrapRunId — 완료 이벤트로 해제.
  const [runningRunId, setRunningRunId] = useState<string | null>(null);

  // Type-A 이중 트리거 차단 — useRealtimeJobs 의 activeJobs 에 LLM_GATE_E running 이
  // 있으면 다른 운영자가 trigger 한 것까지 포함해 추가 실행을 막는다.
  // 권위: 운영 사고 — dashboard-api 가 08:49 + 08:52 두 번 POST 받아 GATE-E 가
  // 2회 발사된 케이스. 위젯의 self-heal 흐름과 정합.
  const { activeJobs } = useRealtimeJobs();
  const gateEInFlight = useMemo(
    () =>
      activeJobs.some(
        (j) => j.jobType === 'LLM_GATE_E' && j.status === 'running'
      ),
    [activeJobs]
  );
  const rebalanceLocked = runningRunId !== null || gateEInFlight;

  // 요약 배너 데이터 소스:
  //   - 정규 섹터 수 N:  useTaxonomyList (길이)
  //   - 원천 섹터 수 M:  매핑 중복 제외 (pageSize=500 / naver_* 전체)
  //   - pending P:       mappings 쿼리 status=pending_review
  //   - unmapped_source_count 최근일 / new_source_sectors_30d:  balance-stats 마지막 행
  const taxonomy = useTaxonomyList({ isActive: true });
  const mappingsAll = useMappingsList({ page: 0, pageSize: 500 });
  const pendingOnly = useMappingsList({
    page: 0,
    pageSize: 1,
    status: ['pending_review'],
  });
  const balanceFrom = shiftIsoDate(7);
  const balanceTo = todayIsoDate();
  const balance = useBalanceStats(balanceFrom, balanceTo);

  const summary = useMemo(() => {
    const canonicalN = taxonomy.data?.length ?? 0;
    const sourceSet = new Set<string>();
    (mappingsAll.data?.items ?? []).forEach((m) =>
      sourceSet.add(`${m.source}:${m.sourceSectorName}`)
    );
    const sourceM = sourceSet.size;
    const consolidation = sourceM > 0 ? canonicalN / sourceM : null;
    const pendingP = pendingOnly.data?.total ?? 0;
    const latest = balance.data?.[balance.data.length - 1];
    const newQ = latest?.newSourceSectors30d ?? 0;
    const unmappedU = latest?.unmappedSourceCount ?? 0;
    return { canonicalN, sourceM, consolidation, pendingP, newQ, unmappedU };
  }, [taxonomy.data, mappingsAll.data, pendingOnly.data, balance.data]);

  const pushToast = useCallback(
    (msg: string, tone: ToastItem['tone'] = 'info') => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, msg, tone }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    },
    []
  );

  const handleTabChange = useCallback(
    (next: TaxonomyTabId) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // WebSocket 구독 — /topic/sector-taxonomy
  useEffect(() => {
    const unsub = subscribe('/topic/sector-taxonomy', (msg) => {
      let payload: TaxonomyEventPayload | null = null;
      try {
        payload = JSON.parse(msg.body) as TaxonomyEventPayload;
      } catch {
        return;
      }
      const code = payload?.event_code ?? '';
      if (code === 'LLM.GATE_E.PENDING_REVIEW_ADDED') {
        pushToast(
          `신규 Type-C 감지: ${payload?.source_sector_name ?? '-'} (conf ${(payload?.confidence ?? 0).toFixed(2)})`,
          'info'
        );
        pendingOnly.refetch();
        mappingsAll.refetch();
      } else if (code === 'LLM.GATE_E.BOOTSTRAP_COMPLETED') {
        pushToast('Type-A 부트스트랩 완료. 리뷰 큐로 이동합니다.', 'success');
        setRunningRunId(null);
        handleTabChange('review');
      }
    });
    return unsub;
    // refetch 는 react-query 내부에서 안정적이지 않을 수 있어 의존성에서 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushToast, handleTabChange]);

  const handleTriggered = useCallback(
    (runId: string) => {
      setRunningRunId(runId);
      pushToast(`Type-A 실행 시작 — run_id=${runId}`, 'success');
      handleTabChange('review');
    },
    [pushToast, handleTabChange]
  );

  return (
    <div className="tx-page">
      <div className="tx-banner">
        <div className="tx-banner__metric">
          <span className="tx-banner__label">정규 섹터</span>
          <span className="tx-banner__value">
            {summary.canonicalN.toLocaleString('ko-KR')}
          </span>
        </div>
        <div className="tx-banner__metric">
          <span className="tx-banner__label">원천 섹터</span>
          <span className="tx-banner__value">
            {summary.sourceM.toLocaleString('ko-KR')}
          </span>
        </div>
        <div className="tx-banner__metric">
          <span className="tx-banner__label">통합도 (N/M)</span>
          <span className="tx-banner__value">
            {summary.consolidation !== null
              ? summary.consolidation.toFixed(2)
              : '—'}
          </span>
        </div>
        <div className="tx-banner__metric">
          <span className="tx-banner__label">검수 대기</span>
          <span className="tx-banner__value">
            {summary.pendingP.toLocaleString('ko-KR')}
          </span>
        </div>
        <div className="tx-banner__metric">
          <span className="tx-banner__label">신규 30d</span>
          <span className="tx-banner__value">
            {summary.newQ.toLocaleString('ko-KR')}
          </span>
        </div>
        <div className="tx-banner__actions">
          <button
            type="button"
            className="pca-btn"
            onClick={() => setHelpOpen(true)}
            aria-label="섹터 택소노미 가이드 열기"
            data-testid="tx-page-help-open"
            title="섹터 택소노미 가이드 (enum · 매핑 흐름 · 커버리지 개념)"
          >
            <HelpCircle size={14} strokeWidth={1.75} />
            가이드
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={() => setRebalanceOpen(true)}
            disabled={rebalanceLocked}
            aria-busy={rebalanceLocked}
            title={
              rebalanceLocked
                ? '현재 GATE-E Type-A 진행 중 (예상 ~9분)'
                : '전체 리밸런싱 실행'
            }
            data-testid="tx-rebalance-trigger"
          >
            {rebalanceLocked ? (
              <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
            ) : (
              <Play size={14} strokeWidth={1.75} />
            )}
            {rebalanceLocked ? '리밸런싱 중...' : '전체 리밸런싱 실행'}
          </button>
        </div>
      </div>

      {runningRunId && (
        <div className="tx-running-banner" role="status" aria-live="polite">
          Type-A 실행 중… (run_id={runningRunId}) — 완료 이벤트 수신 시 리뷰
          큐가 자동 갱신됩니다.
        </div>
      )}

      {summary.unmappedU > 0 && (
        <div className="tx-warn-banner">
          미매핑 원천 섹터 {summary.unmappedU} 건 존재 — 리뷰 큐 &gt; 미매핑
          fallback 탭에서 정리하세요.
        </div>
      )}

      <nav className="tx-tabs" role="tablist" aria-label="섹터 택소노미 탭">
        {TAXONOMY_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`tx-tab${activeTab === tab.id ? ' tx-tab--active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <section>
        {activeTab === 'tree' && <TaxonomyTreeTab pushToast={pushToast} />}
        {activeTab === 'mappings' && <MappingsTab pushToast={pushToast} />}
        {activeTab === 'review' && <ReviewQueueTab pushToast={pushToast} />}
        {activeTab === 'balance' && <BalanceChartsTab />}
        {activeTab === 'audit' && <AuditLogTab />}
        {activeTab === 'feedback' && <FeedbackTab />}
        {activeTab === 'similarity' && <SimilarityHeatmapTab />}
      </section>

      {rebalanceOpen && (
        <RebalanceTriggerModal
          inFlight={rebalanceLocked}
          onClose={() => setRebalanceOpen(false)}
          onTriggered={(runId) => {
            setRebalanceOpen(false);
            handleTriggered(runId);
          }}
          onError={(m) => pushToast(m, 'error')}
        />
      )}

      {helpOpen && <TaxonomyHelpPanel onClose={() => setHelpOpen(false)} />}

      <div className="pca-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pca-toast pca-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
